import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Arrow } from "@faire/web--source/slate/icons/foundation/Arrow";
import { Chevron } from "@faire/web--source/slate/icons/foundation/Chevron";
import { Grid } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { isResponsiveProp } from "@faire/web--source/slate/Theme/ResponsiveProp";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React, { useCallback } from "react";
import styled, { css } from "styled-components";

import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";

import { BaseCarouselProps, getTokensForViewEvent } from "../CarouselBase";

interface IProps<T> extends BaseCarouselProps {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  rows?: number;
  expandable?: boolean;
  gap?: number;
  useArrowToggle?: boolean;
  hideCollapse?: boolean;
  onToggle?: (isGridRowsVisible?: boolean) => void;
  columnTemplate?: (columns: number) => string;
}

export const GridViewBase = <T,>(props: IProps<T>) => {
  const { strictLocalize } = useStrictLocalization();
  const {
    renderItem,
    items,
    rows = 2,
    expandable = true,
    gap = 16,
    useArrowToggle = false,
    hideCollapse = false,
    onToggle,
    columnTemplate,
  } = props;
  const { layoutElement, onVisible, requestId } = React.useContext(Context);
  const [isGridRowsVisible, setIsGridRowsVisible] = React.useState<
    boolean | undefined
  >(undefined);

  const responsiveItemsPerSlide = isResponsiveProp(props.itemsPerSlide)
    ? props.itemsPerSlide
    : { mobileAndAbove: props.itemsPerSlide ?? 2 };
  const fallbackItemsPerSlide = {
    mobileAndAbove: 2,
    tabletAndAbove: 4,
    desktopAndAbove: 5,
  };

  const itemsPerSlide = useResponsiveValue(
    props.itemsPerSlide ? responsiveItemsPerSlide : fallbackItemsPerSlide
  );

  const totalVisibleItems = rows * itemsPerSlide;

  const handleComponentVisible = useCallback(
    (start: number = 0, end?: number) => {
      const endIndex = end;
      const items = getTokensForViewEvent(layoutElement, strictLocalize)?.slice(
        start,
        endIndex
      );
      if (layoutElement.on_click_event_name) {
        onVisible?.(
          layoutElement.on_click_event_name,
          requestId,
          items?.join(" ").slice(0, 255) ?? ""
        );
      }
    },
    [layoutElement, onVisible, requestId, strictLocalize]
  );

  const viewRef = useDoActionWhenVisible(() =>
    handleComponentVisible(0, totalVisibleItems)
  );

  if (items.length < 1) {
    return null;
  }

  return (
    <GridWrapper ref={viewRef}>
      <Grid columns={itemsPerSlide} gap={gap} columnTemplate={columnTemplate}>
        {items
          .slice(0, !expandable ? totalVisibleItems : undefined)
          .map((item, index) => {
            return (
              <GridItemWrapper
                key={`${layoutElement.on_click_event_name}-griditem-${index}`}
                $hidden={!isGridRowsVisible && index >= totalVisibleItems}
              >
                {renderItem(item, index)}
              </GridItemWrapper>
            );
          })}
      </Grid>
      {expandable &&
      items.length > totalVisibleItems &&
      (!hideCollapse || !isGridRowsVisible) ? (
        <ButtonContentWrapper>
          <Button
            variant="tertiary"
            onClick={() => {
              onToggle?.(isGridRowsVisible);
              if (isGridRowsVisible === undefined) {
                setIsGridRowsVisible(true);
                handleComponentVisible(totalVisibleItems);
              } else {
                setIsGridRowsVisible(!isGridRowsVisible);
              }
            }}
          >
            <ShowMoreShowLessToggle
              useArrowToggle={useArrowToggle}
              isGridRowsVisible={isGridRowsVisible ?? false}
              tileNumber={items.length - totalVisibleItems}
            />
          </Button>
        </ButtonContentWrapper>
      ) : null}
    </GridWrapper>
  );
};

const ShowMoreShowLessToggle: React.FC<{
  useArrowToggle: boolean;
  isGridRowsVisible: boolean;
  tileNumber: number;
}> = ({ useArrowToggle, isGridRowsVisible, tileNumber }) => {
  if (useArrowToggle) {
    const upOrDown = isGridRowsVisible ? "up" : "down";
    return (
      <Chevron
        direction={upOrDown}
        data-test-id={`grid-view-base-chevron-${upOrDown}`}
        color={Core.text.subdued}
        width={getSpacing("2x")}
        height={getSpacing("2x")}
      />
    );
  }
  return (
    <>
      {isGridRowsVisible ? (
        <>
          <StrictLocalMsg
            defaultMessage="Show less"
            description={{
              text: "Hide grid items that previously made visible with total number of items to be hidden",
            }}
          />
          <Arrow direction="up" width="16px" height="16px" />
        </>
      ) : (
        <>
          <StrictLocalMsg
            defaultMessage="{tileNumber, plural, one {Show # more} other {Show # more}}"
            description={{
              text: "Show grid items that are more than certain number of rows that was previously made hidden",
              img: "https://cdn.faire.com/fastly/43888c804cb4a00e94c69bdb9293f0276fcb33143979e2580d4b8e4cc726b113.png",
            }}
            values={{
              tileNumber: tileNumber,
            }}
          />
          <Arrow direction="down" width="16px" height="16px" />
        </>
      )}
    </>
  );
};

const ButtonContentWrapper = styled.div`
  width: 100%;
  text-align: center;
  padding-top: ${getSpacing("2x")};
`;

const GridItemWrapper = styled.div<{ $hidden?: boolean }>`
  ${({ $hidden }) =>
    $hidden
      ? css`
          display: none;
        `
      : ""}
`;

const GridWrapper = styled.div`
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing(0, "2x")};
  }
`;
